import React, { useEffect, useState } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { 
  Calendar, FileText, LogOut, Home, CreditCard, DollarSign, 
  CheckCircle, Clock, AlertCircle, MessageSquare, Download,
  ThumbsUp, ThumbsDown, Eye, Star, Send, ChevronRight
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { InvoiceList, PaymentModal, SubscriptionPlans } from '../components/Billing';
import api from '../utils/api';
import { toast } from 'sonner';

const ClientDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [rfps, setRFPs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  useEffect(() => {
    fetchData();
    
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, rfpsRes, invoicesRes, subRes] = await Promise.all([
        api.getProjects(),
        api.getRFPs(),
        api.getInvoices(),
        api.getMySubscription().catch(() => ({ data: { has_subscription: false } })),
      ]);
      setProjects(projectsRes.data);
      setRFPs(rfpsRes.data);
      setInvoices(invoicesRes.data);
      setSubscription(subRes.data.has_subscription ? subRes.data.subscription : null);

      // Get pending approvals for client's projects
      const projectIds = projectsRes.data.map(p => p.project_id);
      const approvalsData = [];
      for (const pid of projectIds.slice(0, 5)) {
        try {
          const res = await api.getProjectApprovals(pid, 'pending');
          approvalsData.push(...res.data);
        } catch {
          // Ignore errors
        }
      }
      setApprovals(approvalsData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const handleDownloadPdf = async (invoiceId) => {
    try {
      const response = await api.downloadInvoicePdf(invoiceId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Invoice downloaded');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const handlePayInvoice = (invoice) => {
    setSelectedInvoice(invoice);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      submitted: 'bg-yellow-500/20 text-yellow-400',
      reviewing: 'bg-blue-500/20 text-blue-400',
      approved: 'bg-green-500/20 text-green-400',
      rejected: 'bg-red-500/20 text-red-400',
      active: 'bg-blue-500/20 text-blue-400',
      completed: 'bg-green-500/20 text-green-400',
      draft: 'bg-gray-500/20 text-gray-400',
      sent: 'bg-blue-500/20 text-blue-400',
      paid: 'bg-green-500/20 text-green-400',
      overdue: 'bg-red-500/20 text-red-400',
      revision_requested: 'bg-orange-500/20 text-orange-400',
    };
    return colors[status] || 'bg-white/10 text-white/60';
  };

  const getPhaseLabel = (phase) => {
    const labels = {
      discovery: 'Discovery',
      concept: 'Concept',
      planning: 'Planning',
      execution: 'Execution',
      post_event: 'Post-Event'
    };
    return labels[phase] || phase;
  };

  const pendingApprovals = approvals.filter(a => a.status === 'pending');
  const pendingInvoices = invoices.filter(i => ['draft', 'sent'].includes(i.status));

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="client-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-[#0A0A0F] border-r border-white/5 p-6 z-50">
        <Link to="/" className="flex items-center space-x-2 mb-10">
          <div className="text-2xl font-bold tracking-tight">
            <span className="text-[#D4AF37]">NEHPETS</span>
            <span className="text-white">+</span>
          </div>
        </Link>

        <div className="space-y-2 mb-10">
          <Link
            to="/dashboard/client"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
          >
            <Home size={20} />
            <span>Overview</span>
          </Link>
          <Link
            to="/dashboard/client/projects"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
          >
            <Calendar size={20} />
            <span>My Events</span>
          </Link>
          <Link
            to="/dashboard/client/approvals"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
          >
            <CheckCircle size={20} />
            <span>Approvals</span>
            {pendingApprovals.length > 0 && (
              <span className="ml-auto bg-[#D4AF37] text-black text-xs px-2 py-0.5 rounded-full">
                {pendingApprovals.length}
              </span>
            )}
          </Link>
          <Link
            to="/dashboard/client/documents"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
          >
            <FileText size={20} />
            <span>Documents</span>
          </Link>
          <Link
            to="/dashboard/client/invoices"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
          >
            <CreditCard size={20} />
            <span>Invoices</span>
            {pendingInvoices.length > 0 && (
              <span className="ml-auto bg-yellow-500 text-black text-xs px-2 py-0.5 rounded-full">
                {pendingInvoices.length}
              </span>
            )}
          </Link>
          <Link
            to="/dashboard/client/messages"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
          >
            <MessageSquare size={20} />
            <span>Messages</span>
          </Link>
          <Link
            to="/dashboard/client/feedback"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
          >
            <Star size={20} />
            <span>Feedback</span>
          </Link>
        </div>

        <div className="absolute bottom-6 left-6 right-6">
          <Button
            onClick={handleLogout}
            className="w-full bg-transparent border border-white/20 text-white hover:bg-white hover:text-black text-xs py-3"
          >
            <LogOut size={16} className="mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        <Routes>
          {/* Overview */}
          <Route
            path="/"
            element={
              <ClientOverview
                user={user}
                projects={projects}
                rfps={rfps}
                invoices={invoices}
                approvals={approvals}
                subscription={subscription}
                loading={loading}
                getStatusColor={getStatusColor}
                getPhaseLabel={getPhaseLabel}
                onPayInvoice={handlePayInvoice}
              />
            }
          />
          
          {/* Projects / Events */}
          <Route
            path="/projects"
            element={
              <ClientProjects
                projects={projects}
                getStatusColor={getStatusColor}
                getPhaseLabel={getPhaseLabel}
              />
            }
          />
          
          {/* Approvals */}
          <Route
            path="/approvals"
            element={
              <ClientApprovals
                approvals={approvals}
                projects={projects}
                getStatusColor={getStatusColor}
                onRefresh={fetchData}
              />
            }
          />
          
          {/* Documents */}
          <Route
            path="/documents"
            element={<ClientDocuments projects={projects} />}
          />
          
          {/* Invoices */}
          <Route
            path="/invoices"
            element={
              <ClientInvoices
                invoices={invoices}
                onPayInvoice={handlePayInvoice}
                onDownloadPdf={handleDownloadPdf}
              />
            }
          />
          
          {/* Messages */}
          <Route path="/messages" element={<ClientMessages projects={projects} />} />
          
          {/* Feedback */}
          <Route
            path="/feedback"
            element={<ClientFeedback projects={projects} />}
          />
        </Routes>
      </main>

      {/* Payment Modal */}
      {selectedInvoice && (
        <PaymentModal
          invoice={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
          onSuccess={() => {
            setSelectedInvoice(null);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

// Client Overview Component
const ClientOverview = ({ 
  user, projects, rfps, invoices, approvals, subscription, 
  loading, getStatusColor, getPhaseLabel, onPayInvoice 
}) => {
  const pendingApprovals = approvals.filter(a => a.status === 'pending');
  const pendingInvoices = invoices.filter(i => ['draft', 'sent'].includes(i.status));
  const totalOutstanding = pendingInvoices.reduce((sum, inv) => sum + (inv.amount || 0), 0);

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-medium mb-2">Welcome back, {user?.full_name}!</h1>
        <p className="text-white/60">Your event dashboard and client portal</p>
      </div>

      {/* Alerts Section */}
      {(pendingApprovals.length > 0 || pendingInvoices.length > 0) && (
        <div className="mb-8 space-y-3">
          {pendingApprovals.length > 0 && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 flex items-center">
              <AlertCircle className="text-yellow-400 mr-3 flex-shrink-0" size={24} />
              <div className="flex-1">
                <p className="font-medium text-yellow-400">
                  {pendingApprovals.length} item{pendingApprovals.length > 1 ? 's' : ''} awaiting your approval
                </p>
                <p className="text-sm text-white/60">Review and approve concepts, budgets, or designs</p>
              </div>
              <Link to="/dashboard/client/approvals">
                <Button size="sm" className="bg-yellow-500 hover:bg-yellow-600 text-black">
                  Review Now
                </Button>
              </Link>
            </div>
          )}
          
          {pendingInvoices.length > 0 && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 flex items-center">
              <CreditCard className="text-blue-400 mr-3 flex-shrink-0" size={24} />
              <div className="flex-1">
                <p className="font-medium text-blue-400">
                  ₦{totalOutstanding.toLocaleString()} outstanding
                </p>
                <p className="text-sm text-white/60">{pendingInvoices.length} invoice{pendingInvoices.length > 1 ? 's' : ''} pending payment</p>
              </div>
              <Link to="/dashboard/client/invoices">
                <Button size="sm" className="bg-blue-500 hover:bg-blue-600 text-white">
                  Pay Now
                </Button>
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
          <Calendar className="text-[#D4AF37] mb-3" size={24} />
          <div className="text-3xl font-bold text-[#D4AF37] mb-2">{projects.length}</div>
          <div className="text-white/60 text-sm uppercase tracking-wider">Active Events</div>
        </div>
        <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
          <Clock className="text-yellow-400 mb-3" size={24} />
          <div className="text-3xl font-bold text-yellow-400 mb-2">{pendingApprovals.length}</div>
          <div className="text-white/60 text-sm uppercase tracking-wider">Pending Approvals</div>
        </div>
        <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
          <CreditCard className="text-blue-400 mb-3" size={24} />
          <div className="text-3xl font-bold text-blue-400 mb-2">
            ₦{totalOutstanding.toLocaleString()}
          </div>
          <div className="text-white/60 text-sm uppercase tracking-wider">Outstanding</div>
        </div>
        <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
          <CheckCircle className="text-green-400 mb-3" size={24} />
          <div className="text-3xl font-bold text-green-400 mb-2">
            {projects.filter(p => p.status === 'completed').length}
          </div>
          <div className="text-white/60 text-sm uppercase tracking-wider">Completed Events</div>
        </div>
      </div>

      {/* Current Events */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-medium">Your Events</h2>
          <Link to="/contact">
            <Button className="bg-[#D4AF37] hover:bg-[#B8963A] text-black text-sm">
              Request New Event
            </Button>
          </Link>
        </div>
        
        {loading ? (
          <div className="text-white/60">Loading...</div>
        ) : projects.length === 0 ? (
          <div className="bg-[#0A0A0F] border border-white/5 p-12 text-center rounded">
            <Calendar className="mx-auto mb-4 text-white/20" size={64} />
            <h3 className="text-xl font-medium mb-2">No Events Yet</h3>
            <p className="text-white/60 mb-4">Submit an RFP to get started with your first event</p>
            <Link to="/contact">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#B8963A]">
                Submit RFP
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {projects.slice(0, 3).map((project) => (
              <div
                key={project.project_id}
                className="bg-[#0A0A0F] border border-white/5 p-6 hover:border-[#D4AF37]/30 transition-colors rounded"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-medium mb-1">{project.project_name}</h3>
                    <p className="text-white/60 text-sm">{project.event_type}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs ${getStatusColor(project.phase)}`}>
                    {getPhaseLabel(project.phase)}
                  </span>
                </div>
                
                {/* Progress Timeline */}
                <div className="flex items-center mb-4">
                  {['discovery', 'concept', 'planning', 'execution', 'post_event'].map((phase, idx) => {
                    const isActive = project.phase === phase;
                    const isPast = ['discovery', 'concept', 'planning', 'execution', 'post_event'].indexOf(project.phase) > idx;
                    return (
                      <React.Fragment key={phase}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs ${
                          isActive ? 'bg-[#D4AF37] text-black' : 
                          isPast ? 'bg-green-500/20 text-green-400' : 
                          'bg-white/10 text-white/40'
                        }`}>
                          {isPast ? '✓' : idx + 1}
                        </div>
                        {idx < 4 && (
                          <div className={`flex-1 h-0.5 ${isPast ? 'bg-green-500/40' : 'bg-white/10'}`} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center text-white/60">
                    <Calendar size={16} className="mr-2" />
                    {project.event_date}
                  </div>
                  <div className="text-[#D4AF37]">
                    Budget: ₦{project.budget?.estimated?.toLocaleString()}
                  </div>
                  <Link to={`/dashboard/client/projects`}>
                    <Button size="sm" variant="ghost" className="text-[#D4AF37]">
                      View Details <ChevronRight size={16} />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Pending Approvals Preview */}
        <div className="bg-[#0A0A0F] border border-white/5 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium">Pending Approvals</h3>
            <Link to="/dashboard/client/approvals" className="text-[#D4AF37] text-sm">
              View All
            </Link>
          </div>
          {pendingApprovals.length === 0 ? (
            <p className="text-white/40 text-sm">No pending approvals</p>
          ) : (
            <div className="space-y-3">
              {pendingApprovals.slice(0, 3).map((approval) => (
                <div key={approval.approval_id} className="flex items-center justify-between p-3 bg-white/5 rounded">
                  <div>
                    <p className="font-medium text-sm">{approval.title}</p>
                    <p className="text-xs text-white/40">{approval.approval_type?.replace('_', ' ')}</p>
                  </div>
                  <Button size="sm" className="bg-[#D4AF37] hover:bg-[#B8963A] text-black text-xs">
                    Review
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Invoices */}
        <div className="bg-[#0A0A0F] border border-white/5 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium">Recent Invoices</h3>
            <Link to="/dashboard/client/invoices" className="text-[#D4AF37] text-sm">
              View All
            </Link>
          </div>
          {invoices.length === 0 ? (
            <p className="text-white/40 text-sm">No invoices yet</p>
          ) : (
            <div className="space-y-3">
              {invoices.slice(0, 3).map((invoice) => (
                <div key={invoice.invoice_id} className="flex items-center justify-between p-3 bg-white/5 rounded">
                  <div>
                    <p className="font-medium text-sm">{invoice.invoice_number}</p>
                    <p className="text-xs text-white/40">Due: {invoice.due_date}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-[#D4AF37] text-sm">₦{invoice.amount?.toLocaleString()}</p>
                    <span className={`text-xs px-2 py-0.5 rounded ${getStatusColor(invoice.status)}`}>
                      {invoice.status?.toUpperCase()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Client Projects Component
const ClientProjects = ({ projects, getStatusColor, getPhaseLabel }) => {
  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-medium mb-2">My Events</h1>
        <p className="text-white/60">Track your event progress and details</p>
      </div>

      {projects.length === 0 ? (
        <div className="bg-[#0A0A0F] border border-white/5 p-12 text-center rounded">
          <Calendar className="mx-auto mb-4 text-white/20" size={64} />
          <p className="text-white/60">No events found</p>
        </div>
      ) : (
        <div className="space-y-6">
          {projects.map((project) => (
            <div key={project.project_id} className="bg-[#0A0A0F] border border-white/5 rounded-lg p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-2xl font-medium mb-1">{project.project_name}</h2>
                  <p className="text-white/60">{project.event_type}</p>
                </div>
                <span className={`px-4 py-2 rounded-full text-sm ${getStatusColor(project.phase)}`}>
                  {getPhaseLabel(project.phase)}
                </span>
              </div>

              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-white/5 p-4 rounded">
                  <p className="text-white/60 text-sm mb-1">Event Date</p>
                  <p className="font-medium">{project.event_date}</p>
                </div>
                <div className="bg-white/5 p-4 rounded">
                  <p className="text-white/60 text-sm mb-1">Location</p>
                  <p className="font-medium">{project.location || 'TBD'}</p>
                </div>
                <div className="bg-white/5 p-4 rounded">
                  <p className="text-white/60 text-sm mb-1">Guest Count</p>
                  <p className="font-medium">{project.guest_count || 'TBD'}</p>
                </div>
                <div className="bg-white/5 p-4 rounded">
                  <p className="text-white/60 text-sm mb-1">Budget</p>
                  <p className="font-medium text-[#D4AF37]">₦{project.budget?.estimated?.toLocaleString()}</p>
                </div>
              </div>

              {/* Phase Timeline */}
              <div className="flex items-center">
                {['discovery', 'concept', 'planning', 'execution', 'post_event'].map((phase, idx) => {
                  const isActive = project.phase === phase;
                  const isPast = ['discovery', 'concept', 'planning', 'execution', 'post_event'].indexOf(project.phase) > idx;
                  return (
                    <React.Fragment key={phase}>
                      <div className="flex flex-col items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isActive ? 'bg-[#D4AF37] text-black' : 
                          isPast ? 'bg-green-500/20 text-green-400' : 
                          'bg-white/10 text-white/40'
                        }`}>
                          {isPast ? <CheckCircle size={20} /> : idx + 1}
                        </div>
                        <span className={`text-xs mt-2 ${isActive ? 'text-[#D4AF37]' : 'text-white/40'}`}>
                          {getPhaseLabel(phase)}
                        </span>
                      </div>
                      {idx < 4 && (
                        <div className={`flex-1 h-0.5 mb-6 ${isPast ? 'bg-green-500/40' : 'bg-white/10'}`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Client Approvals Component
const ClientApprovals = ({ approvals, projects, getStatusColor, onRefresh }) => {
  const [processing, setProcessing] = useState(null);

  const handleApprove = async (approvalId) => {
    setProcessing(approvalId);
    try {
      await api.reviewApproval(approvalId, { status: 'approved', feedback: 'Approved by client' });
      toast.success('Approved successfully');
      onRefresh();
    } catch (error) {
      toast.error('Failed to approve');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (approvalId, feedback) => {
    setProcessing(approvalId);
    try {
      await api.reviewApproval(approvalId, { status: 'rejected', feedback: feedback || 'Rejected by client' });
      toast.success('Rejected');
      onRefresh();
    } catch (error) {
      toast.error('Failed to reject');
    } finally {
      setProcessing(null);
    }
  };

  const getApprovalTypeIcon = (type) => {
    switch (type) {
      case 'concept': return '💡';
      case 'budget': return '💰';
      case 'design': return '🎨';
      case 'vendor_selection': return '🤝';
      case 'program_flow': return '📋';
      default: return '📄';
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-medium mb-2">Approvals</h1>
        <p className="text-white/60">Review and approve concepts, budgets, designs, and more</p>
      </div>

      {approvals.length === 0 ? (
        <div className="bg-[#0A0A0F] border border-white/5 p-12 text-center rounded">
          <CheckCircle className="mx-auto mb-4 text-green-400" size={64} />
          <h3 className="text-xl font-medium mb-2">All Caught Up!</h3>
          <p className="text-white/60">No pending approvals at this time</p>
        </div>
      ) : (
        <div className="space-y-4">
          {approvals.map((approval) => {
            const project = projects.find(p => p.project_id === approval.project_id);
            
            return (
              <div key={approval.approval_id} className="bg-[#0A0A0F] border border-white/5 rounded-lg p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center text-2xl">
                      {getApprovalTypeIcon(approval.approval_type)}
                    </div>
                    <div>
                      <h3 className="text-lg font-medium mb-1">{approval.title}</h3>
                      <p className="text-white/60 text-sm mb-2">
                        {approval.approval_type?.replace('_', ' ').toUpperCase()} • {project?.project_name}
                      </p>
                      <p className="text-white/70 text-sm">{approval.description}</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs ${getStatusColor(approval.status)}`}>
                    {approval.status?.replace('_', ' ').toUpperCase()}
                  </span>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-white/5">
                  <div className="text-sm text-white/40">
                    Submitted by {approval.submitted_by_name} • {new Date(approval.submitted_at).toLocaleDateString()}
                  </div>
                  
                  {approval.status === 'pending' && (
                    <div className="flex space-x-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReject(approval.approval_id)}
                        disabled={processing === approval.approval_id}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        <ThumbsDown size={16} className="mr-1" />
                        Request Changes
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleApprove(approval.approval_id)}
                        disabled={processing === approval.approval_id}
                        className="bg-green-500 hover:bg-green-600 text-white"
                      >
                        <ThumbsUp size={16} className="mr-1" />
                        Approve
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Client Documents Component
const ClientDocuments = ({ projects }) => {
  // Mock documents for display
  const documents = [
    { id: 1, name: 'Event Proposal - Tech Corp Gala', type: 'proposal', date: '2024-12-15', project: 'Tech Corp Annual Gala' },
    { id: 2, name: 'Budget Breakdown v2', type: 'budget', date: '2024-12-18', project: 'Tech Corp Annual Gala' },
    { id: 3, name: 'Event Contract', type: 'contract', date: '2024-12-20', project: 'Tech Corp Annual Gala' },
  ];

  const getTypeIcon = (type) => {
    switch (type) {
      case 'proposal': return '📋';
      case 'budget': return '💰';
      case 'contract': return '📝';
      case 'design': return '🎨';
      default: return '📄';
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-medium mb-2">Documents</h1>
        <p className="text-white/60">Access your proposals, budgets, contracts, and designs</p>
      </div>

      <div className="space-y-4">
        {documents.map((doc) => (
          <div key={doc.id} className="bg-[#0A0A0F] border border-white/5 rounded-lg p-4 flex items-center justify-between hover:border-[#D4AF37]/30 transition-all">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white/5 rounded-lg flex items-center justify-center text-2xl">
                {getTypeIcon(doc.type)}
              </div>
              <div>
                <h4 className="font-medium">{doc.name}</h4>
                <p className="text-sm text-white/60">{doc.project} • {doc.date}</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <Button size="sm" variant="ghost">
                <Eye size={16} className="mr-1" /> View
              </Button>
              <Button size="sm" variant="ghost">
                <Download size={16} className="mr-1" /> Download
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Client Invoices Component
const ClientInvoices = ({ invoices, onPayInvoice, onDownloadPdf }) => {
  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-medium mb-2">Invoices</h1>
        <p className="text-white/60">View and pay your invoices</p>
      </div>

      <InvoiceList
        invoices={invoices}
        onPayInvoice={onPayInvoice}
        onDownloadPdf={onDownloadPdf}
      />
    </div>
  );
};

// Client Messages Component  
const ClientMessages = ({ projects }) => {
  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-medium mb-2">Messages</h1>
        <p className="text-white/60">Communicate with your project manager</p>
      </div>

      <div className="bg-[#0A0A0F] border border-white/5 p-12 text-center rounded">
        <MessageSquare className="mx-auto mb-4 text-white/20" size={64} />
        <h3 className="text-xl font-medium mb-2">Messaging Coming Soon</h3>
        <p className="text-white/60">Direct messaging with your PM will be available shortly</p>
      </div>
    </div>
  );
};

// Client Feedback Component
const ClientFeedback = ({ projects }) => {
  const [form, setForm] = useState({
    projectId: '',
    rating: 5,
    feedback: '',
    category: 'service'
  });
  const [submitting, setSubmitting] = useState(false);

  const completedProjects = projects.filter(p => p.status === 'completed' || p.phase === 'post_event');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.projectId) {
      toast.error('Please select an event');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.submitFeedback({
        project_id: form.projectId,
        rating: form.rating,
        feedback_text: form.feedback,
        category: form.category,
        is_anonymous: false
      });
      toast.success('Thank you for your feedback!');
      setForm({ projectId: '', rating: 5, feedback: '', category: 'service' });
    } catch (error) {
      toast.error('Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-medium mb-2">Feedback</h1>
        <p className="text-white/60">Share your experience and help us improve</p>
      </div>

      <div className="max-w-xl">
        <form onSubmit={handleSubmit} className="bg-[#0A0A0F] border border-white/5 rounded-lg p-6 space-y-6">
          <div>
            <label className="text-white/80 text-sm block mb-2">Select Event *</label>
            <select
              value={form.projectId}
              onChange={(e) => setForm({ ...form, projectId: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded px-4 py-3 text-white"
              required
            >
              <option value="">Choose an event...</option>
              {projects.map((p) => (
                <option key={p.project_id} value={p.project_id}>
                  {p.project_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-white/80 text-sm block mb-2">Rating</label>
            <div className="flex space-x-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setForm({ ...form, rating: star })}
                  className="p-1 focus:outline-none"
                >
                  <Star
                    size={32}
                    className={star <= form.rating ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-white/20'}
                  />
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-white/80 text-sm block mb-2">Category</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded px-4 py-3 text-white"
            >
              <option value="service">Service Quality</option>
              <option value="communication">Communication</option>
              <option value="creativity">Creativity</option>
              <option value="value">Value for Money</option>
              <option value="general">General</option>
            </select>
          </div>

          <div>
            <label className="text-white/80 text-sm block mb-2">Your Feedback</label>
            <textarea
              value={form.feedback}
              onChange={(e) => setForm({ ...form, feedback: e.target.value })}
              rows={4}
              placeholder="Tell us about your experience..."
              className="w-full bg-white/5 border border-white/10 rounded px-4 py-3 text-white"
            />
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#D4AF37] hover:bg-[#B8963A] text-black py-4"
          >
            {submitting ? 'Submitting...' : 'Submit Feedback'}
            <Send size={16} className="ml-2" />
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ClientDashboard;
